import { useCallback, useEffect, useRef, useState } from "react";
import ShapeEditorTools from "./ShapeEditorTools.jsx";

const CAMERA_SECTIONS = [
  {
    key: "transform",
    title: "Transform",
    fields: [
      { key: "x", label: "X" },
      { key: "y", label: "Y" },
      { key: "z", label: "Z" },
      { key: "zoom", label: "Zoom" },
      { key: "rotation", label: "Rot" },
      { key: "originX", label: "OX" },
      { key: "originY", label: "OY" },
    ],
  },
  {
    key: "position-physics",
    title: "Position Physics",
    fields: [
      { key: "xVelocity", label: "VX" },
      { key: "yVelocity", label: "VY" },
      { key: "zVelocity", label: "VZ" },
      { key: "xAcceleration", label: "AX" },
      { key: "yAcceleration", label: "AY" },
      { key: "zAcceleration", label: "AZ" },
    ],
  },
  {
    key: "lens-physics",
    title: "Lens Physics",
    fields: [
      { key: "zoomVelocity", label: "Zoom V" },
      { key: "zoomAcceleration", label: "Zoom A" },
      { key: "rotationVelocity", label: "Rot V" },
      { key: "rotationAcceleration", label: "Rot A" },
    ],
  },
];

const formatCoordinateForClipboard = (value) => {
  const parsed = Number(String(value ?? "").trim());
  if (!Number.isFinite(parsed)) return null;
  const rounded = Math.round(parsed * 100) / 100;
  return Number.isInteger(rounded)
    ? String(rounded)
    : rounded.toFixed(2).replace(/\.?0+$/, "");
};

const EditorToolsBar = ({
  activeTab = "all",
  location = { x: "", y: "" },
  isLocationPicking = false,
  onLocationInputChange,
  onToggleLocationPicking,
  camera = {},
  isCameraOverrideActive = false,
  onCameraInputChange,
  onSyncCameraToLive,
  onResetCameraOverride,
  shapes = [],
  selectedShapeId = null,
  selectedShapeIds = [],
  onSelectShape,
  onAddShape,
  onCloneShape,
  onCopyShapes,
  onPasteShapes,
  onDeleteShape,
  onResetShape,
  onShapeFieldChange,
  buildSelectedShapeDsl,
  buildAllShapesDsl,
  onInsertScriptSnippet,
}) => {
  const [pickedHex, setPickedHex] = useState("#ffffff");
  const [isPicking, setIsPicking] = useState(false);
  const [copyState, setCopyState] = useState({
    tool: "",
    type: "",
    message: "",
  });
  const copyTimerRef = useRef(null);
  const hasEyeDropper = typeof window !== "undefined" && "EyeDropper" in window;

  useEffect(() => {
    return () => {
      window.clearTimeout(copyTimerRef.current);
    };
  }, []);

  const copyHex = useCallback((value) => {
    const text = String(value || "").trim();
    if (!text) return;
    navigator.clipboard?.writeText(text).catch(() => {});
  }, []);

  const handleCopy = useCallback(async (tool, value, successMessage) => {
    const text = String(value || "").trim();
    if (!text) return;

    try {
      if (!navigator.clipboard?.writeText) {
        throw new Error("Clipboard unavailable");
      }
      await navigator.clipboard.writeText(text);
      setCopyState({
        tool,
        type: "success",
        message: successMessage,
      });
    } catch {
      setCopyState({
        tool,
        type: "error",
        message: "Copy failed",
      });
    }

    window.clearTimeout(copyTimerRef.current);
    copyTimerRef.current = window.setTimeout(() => {
      setCopyState({ tool: "", type: "", message: "" });
    }, 1800);
  }, []);

  const getCopyLabel = useCallback(
    (tool) => {
      if (copyState.tool !== tool) return "Copy";
      if (copyState.type === "success") return "Copied";
      if (copyState.type === "error") return "Failed";
      return "Copy";
    },
    [copyState],
  );

  const handleColorChange = useCallback(
    (value) => {
      const next = String(value || "").trim();
      if (!next) return;
      setPickedHex(next);
      copyHex(next);
    },
    [copyHex],
  );

  const handleEyeDropper = useCallback(async () => {
    if (!window.EyeDropper) return;
    try {
      setIsPicking(true);
      const result = await new window.EyeDropper().open();
      if (result?.sRGBHex) {
        handleColorChange(result.sRGBHex);
      }
    } finally {
      setIsPicking(false);
    }
  }, [handleColorChange]);

  const locationCopyValue = (() => {
    const x = formatCoordinateForClipboard(location?.x);
    const y = formatCoordinateForClipboard(location?.y);
    if (!x || !y) return "";
    return `x=${x},y=${y}`;
  })();

  const cameraCopyValue = (() => {
    const x = formatCoordinateForClipboard(camera?.x);
    const y = formatCoordinateForClipboard(camera?.y);
    const z = formatCoordinateForClipboard(camera?.z);
    const zoom = formatCoordinateForClipboard(camera?.zoom);
    const rotation = formatCoordinateForClipboard(camera?.rotation);
    const originX = formatCoordinateForClipboard(camera?.originX);
    const originY = formatCoordinateForClipboard(camera?.originY);
    const xVelocity = formatCoordinateForClipboard(camera?.xVelocity);
    const yVelocity = formatCoordinateForClipboard(camera?.yVelocity);
    const zVelocity = formatCoordinateForClipboard(camera?.zVelocity);
    const xAcceleration = formatCoordinateForClipboard(camera?.xAcceleration);
    const yAcceleration = formatCoordinateForClipboard(camera?.yAcceleration);
    const zAcceleration = formatCoordinateForClipboard(camera?.zAcceleration);
    const zoomVelocity = formatCoordinateForClipboard(camera?.zoomVelocity);
    const zoomAcceleration = formatCoordinateForClipboard(camera?.zoomAcceleration);
    const rotationVelocity = formatCoordinateForClipboard(camera?.rotationVelocity);
    const rotationAcceleration = formatCoordinateForClipboard(camera?.rotationAcceleration);
    if (
      !x ||
      !y ||
      !z ||
      !zoom ||
      !rotation ||
      !originX ||
      !originY ||
      !xVelocity ||
      !yVelocity ||
      !zVelocity ||
      !xAcceleration ||
      !yAcceleration ||
      !zAcceleration ||
      !zoomVelocity ||
      !zoomAcceleration ||
      !rotationVelocity ||
      !rotationAcceleration
    ) {
      return "";
    }
    return [
      `cameraPosition|x=${x},y=${y},z=${z};`,
      `cameraZoom|value=${zoom};`,
      `cameraRotation|value=${rotation};`,
      `cameraOrigin|x=${originX},y=${originY};`,
      `cameraPositionPhysics|velocityX=${xVelocity},velocityY=${yVelocity},velocityZ=${zVelocity},accelerationX=${xAcceleration},accelerationY=${yAcceleration},accelerationZ=${zAcceleration};`,
      `cameraZoomPhysics|velocity=${zoomVelocity},acceleration=${zoomAcceleration};`,
      `cameraRotationPhysics|velocity=${rotationVelocity},acceleration=${rotationAcceleration};`,
    ].join("\n");
  })();

  return (
    <div className="editor-tools-bar">
      <ShapeEditorTools
        activeTab={activeTab}
        shapes={shapes}
        selectedShapeId={selectedShapeId}
        selectedShapeIds={selectedShapeIds}
        onSelectShape={onSelectShape}
        onAddShape={onAddShape}
        onCloneShape={onCloneShape}
        onCopyShapes={onCopyShapes}
        onPasteShapes={onPasteShapes}
        onDeleteShape={onDeleteShape}
        onResetShape={onResetShape}
        onShapeFieldChange={onShapeFieldChange}
        buildSelectedShapeDsl={buildSelectedShapeDsl}
        buildAllShapesDsl={buildAllShapesDsl}
        onInsertScriptSnippet={onInsertScriptSnippet}
      />
      {(activeTab === "all" || activeTab === "color") && (
        <div className="editor-tools-group">
          <span className="editor-tools-label">Color</span>
          <input
            type="color"
            className="editor-tools-color-input"
            value={pickedHex}
            onChange={(event) => handleColorChange(event.target.value)}
            aria-label="Pick color"
          />
          <input
            type="text"
            className="editor-tools-color-hex"
            value={pickedHex}
            onChange={(event) => handleColorChange(event.target.value)}
            aria-label="Hex color"
          />
          <button
            type="button"
            className="editor-tools-btn"
            onClick={() => handleCopy("color", pickedHex, "Color copied")}
          >
            {getCopyLabel("color")}
          </button>
          {hasEyeDropper && (
            <button
              type="button"
              className="editor-tools-btn"
              onClick={handleEyeDropper}
              disabled={isPicking}
            >
              {isPicking ? "Picking..." : "Pick"}
            </button>
          )}
          {copyState.tool === "color" && copyState.message ? (
            <p
              className={`animation-workbench-copy-status editor-tools-copy-status ${copyState.type}`}
            >
              {copyState.message}
            </p>
          ) : null}
        </div>
      )}
      {(activeTab === "all" || activeTab === "location") && (
        <div className="editor-tools-group">
          <span className="editor-tools-label">Location</span>
          <label className="editor-tools-coordinate-field">
            <span>X</span>
            <input
              type="text"
              inputMode="decimal"
              className="editor-tools-coordinate-input"
              value={location?.x ?? ""}
              onChange={(event) =>
                onLocationInputChange?.("x", event.target.value)
              }
              aria-label="World X coordinate"
            />
          </label>
          <label className="editor-tools-coordinate-field">
            <span>Y</span>
            <input
              type="text"
              inputMode="decimal"
              className="editor-tools-coordinate-input"
              value={location?.y ?? ""}
              onChange={(event) =>
                onLocationInputChange?.("y", event.target.value)
              }
              aria-label="World Y coordinate"
            />
          </label>
          <button
            type="button"
            className="editor-tools-btn"
            onClick={() =>
              handleCopy("location", locationCopyValue, "Location copied")
            }
            disabled={!locationCopyValue}
          >
            {getCopyLabel("location")}
          </button>
          <button
            type="button"
            className={`editor-tools-btn ${isLocationPicking ? "active" : ""}`}
            onClick={onToggleLocationPicking}
          >
            {isLocationPicking ? "Cancel" : "Pick"}
          </button>
          {isLocationPicking && (
            <span className="editor-tools-hint">Click the preview</span>
          )}
          {copyState.tool === "location" && copyState.message ? (
            <p
              className={`animation-workbench-copy-status editor-tools-copy-status ${copyState.type}`}
            >
              {copyState.message}
            </p>
          ) : null}
        </div>
      )}
      {(activeTab === "all" || activeTab === "camera") && (
        <div className="editor-tools-group editor-tools-group-camera">
          <div className="editor-tools-group-header">
            <div className="editor-tools-group-heading">
              <span className="editor-tools-group-title">Camera</span>
              {isCameraOverrideActive ? (
                <span className="editor-tools-hint">Override</span>
              ) : (
                <span className="editor-tools-hint">Live</span>
              )}
            </div>
            <div className="editor-tools-group-actions">
              <button
                type="button"
                className="editor-tools-btn"
                onClick={() => handleCopy("camera", cameraCopyValue, "Camera copied")}
                disabled={!cameraCopyValue}
              >
                {getCopyLabel("camera")}
              </button>
              <button
                type="button"
                className="editor-tools-btn"
                onClick={onSyncCameraToLive}
              >
                Sync
              </button>
              <button
                type="button"
                className="editor-tools-btn"
                onClick={onResetCameraOverride}
              >
                Reset
              </button>
            </div>
          </div>
          <div className="editor-tools-group-body">
            <div className="editor-tools-camera-grid">
              {CAMERA_SECTIONS.map((section) => (
                <div key={section.key} className="editor-tools-subsection">
                  <span className="editor-tools-subsection-title">
                    {section.title}
                  </span>
                  <div className="editor-tools-subsection-fields">
                    {section.fields.map((field) => (
                      <label
                        key={field.key}
                        className="editor-tools-coordinate-field"
                      >
                        <span>{field.label}</span>
                        <input
                          type="text"
                          inputMode="decimal"
                          className="editor-tools-coordinate-input"
                          value={camera?.[field.key] ?? ""}
                          onChange={(event) =>
                            onCameraInputChange?.(field.key, event.target.value)
                          }
                          aria-label={`Camera ${field.key}`}
                        />
                      </label>
                    ))}
                  </div>
                </div>
              ))}
            </div>
            {copyState.tool === "camera" && copyState.message ? (
              <p
                className={`animation-workbench-copy-status editor-tools-copy-status ${copyState.type}`}
              >
                {copyState.message}
              </p>
            ) : null}
          </div>
        </div>
      )}
    </div>
  );
};

export default EditorToolsBar;
